import React, { useState } from 'react';
import { Calendar, X } from 'lucide-react';
import { format, subDays } from 'date-fns';
import { DateRange } from 'react-day-picker';

import { Button } from '@/components/ui/button';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

interface DateRangePickerProps {
  dateRange: DateRange | undefined;
  onDateRangeChange: (range: DateRange | undefined) => void;
  className?: string;
}

interface DatePreset {
  label: string;
  range: DateRange;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({ dateRange, onDateRangeChange, className }) => {
  const [isOpen, setIsOpen] = useState(false);

  const today = new Date();

  const presets: DatePreset[] = [
    {
      label: 'Today',
      range: { from: today, to: today },
    },
    {
      label: 'Yesterday',
      range: { from: subDays(today, 1), to: subDays(today, 1) },
    },
    {
      label: 'Last 7 days',
      range: { from: subDays(today, 6), to: today },
    },
    {
      label: 'Last 14 days',
      range: { from: subDays(today, 13), to: today },
    },
    {
      label: 'Last 30 days',
      range: { from: subDays(today, 29), to: today },
    },
  ];

  const handlePresetClick = (preset: DatePreset) => {
    onDateRangeChange(preset.range);
    setIsOpen(false);
  };

  const handleClearRange = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDateRangeChange(undefined);
  };

  const formatDateRange = (range: DateRange | undefined) => {
    if (!range?.from) return 'Select date range';

    if (!range.to) {
      return format(range.from, 'MMM dd, yyyy');
    }

    if (range.from.toDateString() === range.to.toDateString()) {
      return format(range.from, 'MMM dd, yyyy');
    }

    if (range.from.getFullYear() === range.to.getFullYear()) {
      return `${format(range.from, 'MMM dd')} - ${format(range.to, 'MMM dd, yyyy')}`;
    }

    return `${format(range.from, 'MMM dd, yyyy')} - ${format(range.to, 'MMM dd, yyyy')}`;
  };

  const isRangeSelected = dateRange?.from && dateRange?.to;

  return (
    <div className={cn('relative', className)}>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant='outline'
            className={cn(
              'w-full sm:w-64 justify-start text-left font-normal',
              !dateRange?.from && 'text-muted-foreground',
              isRangeSelected && 'border-primary/50 bg-primary/5'
            )}
          >
            <Calendar className='mr-2 h-4 w-4 shrink-0' />
            <span className='truncate'>{formatDateRange(dateRange)}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className='w-auto p-0' align='start' side='bottom'>
          <div className='flex flex-col sm:flex-row'>
            <div className='border-b sm:border-b-0 sm:border-r border-border'>
              <div className='p-3'>
                <h4 className='text-sm font-medium mb-2'>Quick Select</h4>
                <div className='grid gap-1 min-w-[140px]'>
                  {presets.map(preset => (
                    <Button
                      key={preset.label}
                      variant='ghost'
                      size='sm'
                      className='justify-start h-8 px-2 font-normal'
                      onClick={() => handlePresetClick(preset)}
                    >
                      {preset.label}
                    </Button>
                  ))}
                </div>

                {dateRange?.from && (
                  <div className='mt-3 pt-3 border-t border-border'>
                    <Button
                      variant='ghost'
                      size='sm'
                      className='justify-start h-8 px-2 font-normal text-muted-foreground hover:text-destructive'
                      onClick={() => {
                        onDateRangeChange(undefined);
                        setIsOpen(false);
                      }}
                    >
                      <X className='mr-2 h-3 w-3' />
                      Clear selection
                    </Button>
                  </div>
                )}
              </div>
            </div>

            <div className='p-3'>
              <CalendarComponent
                initialFocus
                mode='range'
                defaultMonth={dateRange?.from || today}
                selected={dateRange}
                onSelect={range => {
                  onDateRangeChange(range);
                  if (range?.from && range?.to) {
                    setIsOpen(false);
                  }
                }}
                numberOfMonths={1}
                className='rounded-md'
              />
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {isRangeSelected && (
        <Button
          variant='ghost'
          size='icon'
          className='absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 hover:bg-destructive/10 hover:text-destructive'
          onClick={handleClearRange}
        >
          <X className='h-3 w-3' />
        </Button>
      )}
    </div>
  );
};

export default DateRangePicker;
